import popupSound from '@/assets/sounds/popup_sound.mp3';

/**
 * Utility class for playing, pausing, and stopping audio files.
 * Wraps the native `HTMLAudioElement` for easier control over sounds in the app.
 */
export class SoundPlayer {
  private audio: HTMLAudioElement;
  private isUnlocked = false;

  /**
   * Creates a new SoundPlayer instance with the given audio source.
   * @param src - Path or URL to the audio file.
   */
  constructor(src: string) {
    this.audio = new Audio(src);
    this.audio.preload = 'auto';
    this.audio.muted = true;
  }

  /**
   * Unlocks the audio for playback in Safari/iOS silently.
   * This must be called from a user gesture event handler (e.g., 'click').
   */
  unlock = () => {
    if (this.isUnlocked) return;
    this.audio.muted = false;
    this.isUnlocked = true;
  };

  /**
   * Plays the audio from the start.
   * If already playing, it will restart the sound.
   * @returns A promise that resolves when playback starts successfully.
   */
  play = async () => {
    try {
      this.audio.currentTime = 0;
      await this.audio.play();
    } catch (err) {
      console.warn('Sound play failed:', err);
    }
  };

  /**
   * Stops the audio and resets playback position to the beginning.
   */
  stop = () => {
    this.audio.pause();
    this.audio.currentTime = 0;
  };

  /**
   * Pauses the audio, keeping the current playback position.
   */
  pause = () => {
    this.audio.pause();
  };

  /**
   * Sets the audio volume.
   * @param volume - Volume level between 0.0 (silent) and 1.0 (full volume).
   */
  setVolume = (volume: number) => {
    this.audio.volume = Math.min(Math.max(volume, 0), 1);
  };

  /**
   * Checks whether the audio is currently playing.
   * @returns `true` if the audio is playing, otherwise `false`.
   */
  isPlaying = () => {
    return !this.audio.paused;
  };
}

export const popupSoundPlayer = new SoundPlayer(popupSound);
